import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from '@tanstack/react-form'
import { Container, Typography, Card, TextField } from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchGroups,
  fetchGroupsV2,
  fetchLightweightUsersV4,
  fetchLinkedServices,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  selectLinkedServicesStatus,
} from '../manage.slice'
import UsersDataGrid from './components/users-data-grid'
import { GroupFormData, groupSchema } from './validation'
import { paperInputSx } from '../styles'
import {
  transformUsersToRows,
  filterUsers,
  extractLicenseTypes,
} from './utils/user-transforms'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import CreateFooterActionButtons from '../components/cancel-save-buttons'
import { createGroup } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { GROUPS_URLS } from '../constants'

export default function GroupCreateV4(): ReactElement {
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()

  let linkedServiceID = (location.state as { linkedServiceID?: string })
    ?.linkedServiceID
  let linkedServiceName = useLinkedServiceName(linkedServiceID)

  let users = useAppSelector(
    selectLightweightUsersV4ByService(linkedServiceID || ''),
  )
  let fetchUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(linkedServiceID || ''),
  )
  let fetchLinkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let [selectedUsers, setSelectedUsers] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })
  let [licenseTypeFilter, setLicenseTypeFilter] = useState('')
  let [searchQuery, setSearchQuery] = useState('')

  let handleCancel = useCallback(() => {
    navigate(GROUPS_URLS.INDEX, {
      state: { linkedServiceID },
    })
  }, [linkedServiceID, navigate])

  let userRows = useMemo(() => transformUsersToRows(users), [users])
  let availableLicenseTypes = useMemo(
    () => extractLicenseTypes(userRows),
    [userRows],
  )
  // Filter users (always include selected users)
  let filteredUsers = useMemo(
    () =>
      filterUsers(
        userRows,
        Array.from(selectedUsers.ids) as string[],
        licenseTypeFilter,
        searchQuery,
      ),
    [userRows, licenseTypeFilter, searchQuery, selectedUsers],
  )

  let form = useForm({
    defaultValues: {
      name: '',
      userIDs: [],
    } as GroupFormData,
    validators: {
      onMount: groupSchema,
      onChange: groupSchema,
    },
    onSubmit: async ({ value }) => {
      if (!linkedServiceID) return

      let payload = {
        name: value.name,
        linkedServiceID,
        userIDs: value.userIDs,
      }

      await createGroup(payload)
        .then(() => dispatch(fetchGroups()))
        .then(() => dispatch(fetchGroupsV2(linkedServiceID)))
        .then(() => {
          navigate(GROUPS_URLS.INDEX, {
            state: {
              successMessage: `Group "${value.name}" created successfully`,
              linkedServiceID,
            },
          })
        })
        .catch((err: any) => {
          // eslint-disable-next-line no-console
          console.error(
            `Error creating group ${value.name}. `,
            'Payload: ',
            payload,
            'Error: ',
            err,
          )
          showError(`Error creating group ${value.name}.`)
        })
    },
  })

  let handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setSelectedUsers(newSelection)

      // Calculate selected IDs based on selection model type
      let selectedIDs =
        newSelection.type === 'exclude'
          ? (() => {
              // Select all: get all visible IDs except excluded ones
              let excludedIDs = newSelection.ids
                ? (Array.from(newSelection.ids) as string[])
                : []
              return filteredUsers
                .map((row) => row.id)
                .filter((id) => !excludedIDs.includes(id))
            })()
          : newSelection.ids
            ? (Array.from(newSelection.ids) as string[])
            : []

      form.setFieldValue('userIDs', selectedIDs)
      form.validate('change')
    },
    [filteredUsers, form],
  )

  let handleResetFilters = useCallback(() => {
    setSearchQuery('')
    setLicenseTypeFilter('')
  }, [])

  let isLoading = fetchUsersStatus === 'pending'

  useAutoFetch(fetchLinkedServicesStatus, fetchLinkedServices)

  // Keep conditional fetch in useEffect (depends on linkedServiceID)
  useEffect(() => {
    if (linkedServiceID && fetchUsersStatus === 'uninitialized') {
      dispatch(fetchLightweightUsersV4(linkedServiceID))
    }
  }, [linkedServiceID, fetchUsersStatus, dispatch])

  useEffect(() => {
    if (fetchLinkedServicesStatus === 'fulfilled' && !linkedServiceID) {
      handleCancel()
    }
  }, [linkedServiceID, fetchLinkedServicesStatus, navigate])

  return (
    <Container maxWidth="xl" className="py-6">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        aria-label="Create group form"
      >
        <PageTitleWithLinkedService
          pageTitle="Create Group"
          linkedServiceName={linkedServiceName ?? undefined}
          moduleName="Groups"
          handleBackClick={handleCancel}
          className="mb-6"
        />

        <Card className="p-6 mb-6">
          <Typography variant="h6" className="mb-4">
            Group Name
          </Typography>
          <form.Field name="name">
            {(field) => (
              <TextField
                fullWidth
                required
                label="Name"
                placeholder="Enter group name..."
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={shouldShowZodFieldError(field.state.meta)}
                helperText={
                  shouldShowZodFieldError(field.state.meta)
                    ? getZodValidationError(field.state.meta.errors)
                    : undefined
                }
                aria-label="Group name"
                aria-invalid={shouldShowZodFieldError(field.state.meta)}
                sx={paperInputSx}
              />
            )}
          </form.Field>
        </Card>
        <form.Field name="userIDs">
          {(field) => (
            <UsersDataGrid
              users={filteredUsers}
              selectedUsers={selectedUsers}
              onSelectionChange={handleSelectionChange}
              searchQuery={searchQuery}
              licenseTypeFilter={licenseTypeFilter}
              availableLicenseTypes={availableLicenseTypes}
              onSearchChange={setSearchQuery}
              onLicenseTypeChange={setLicenseTypeFilter}
              onResetFilters={handleResetFilters}
              isLoading={isLoading}
              field={field}
              editing={true}
              title="Select Users"
            />
          )}
        </form.Field>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <CreateFooterActionButtons
              moduleName="Group"
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              isDisabled={isLoading || isSubmitting}
              handleSave={form.handleSubmit}
              handleCancel={handleCancel}
            />
          )}
        </form.Subscribe>
      </form>
      {NotificationComponent}
    </Container>
  )
}
